// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SolutionsCard from "@guestsupply/App/Components/SolutionsCard/SolutionsCard";
import XngCarousel from "@guestsupply/App/Components/XngCarousel/XngCarousel";
import xngGaGeneralEventEvent from "@guestsupply/App/Handlers/GoogleAnalytics/GeneralEvents/GeneralEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { sectionTitleFields } from "@guestsupply/App/Widgets/SectionTitle/SectionTitle";
import { fontFamilyValues, GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    solutions = "solutions",
    title = "title",
    backgroundColor = "backgroundColor",
    viewMoreText = "viewMoreText",
    viewMoreLink = "viewMoreLink",
    border = "border",
    fontFamily = "fontFamily",
}

const enum solutionFields {
    buttonText = "buttonText",
    title = "title",
    image = "image",
    destination = "destination",
    backgroundColor = "backgroundColor",
}

interface SolutionsListProps {
    fields: {
        [solutionFields.title]: string;
        [solutionFields.image]: string;
        [solutionFields.destination]: LinkFieldValue;
        [solutionFields.buttonText]: string;
        [fields.backgroundColor]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.solutions]: SolutionsListProps[];
        [fields.title]: string;
        [fields.backgroundColor]: string;
        [fields.viewMoreLink]: string | LinkFieldValue;
        [fields.viewMoreText]: string;
        [fields.border]: "top" | "bottom" | "none";
        [fields.fontFamily]: fontFamilyValues.GOTHAM | fontFamilyValues.BODONI;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    // xng-change: add google event
    xGoogleAnalytics: state.xGoogleAnalytics,
    currentBillTo: getCurrentBillToState(state).value,
    currentPage: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    // xng-change: add google event
    xngGaGeneralEventEvent,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SolutionsStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
}

export const solutionsStyles: SolutionsStyles = {};

const styles = solutionsStyles;

const SolutionsWidget: React.FC<Props> = ({
    fields: { backgroundColor, solutions, title, viewMoreLink, viewMoreText, border, fontFamily },
    history,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaGeneralEventEvent,
}) => {
    const { isInShell } = useShellContext();
    const { isTabletScreen, isMobileScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    }));
    if (!solutions) {
        return null;
    }
    const handleButtonClick = (url: string) => {
        if (!url) {
            return;
        }

        if (url.includes("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const showNavigationAndPagination =
        (isMobileScreen && solutions.length > 1) || (isTabletScreen && solutions.length > 2) || solutions.length > 4;

    return (
        <StyledWrapper {...styles.container} {...{ backgroundColor }}>
            <StyledWrapper {...styles.wrapper}>
                {createWidgetElement("SectionTitle/SectionTitle", {
                    fields: {
                        title,
                        viewMoreText,
                        viewMoreLink,
                        border,
                        fontFamily,
                    },
                })}
                <XngCarousel
                    navigation={showNavigationAndPagination}
                    draggable={false}
                    scrollbar={false}
                    pagination={showNavigationAndPagination}
                    breakpoints={{
                        480: {
                            slidesPerView: 1,
                        },
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 20,
                        },
                        1024: {
                            slidesPerView: 4,
                            spaceBetween: 22,
                        },
                    }}
                >
                    {solutions?.map(solution => {
                        return (
                            <SolutionsCard
                                key={solution.fields.title}
                                title={solution.fields.title}
                                destination={solution.fields.destination}
                                image={solution.fields.image}
                                handleButtonClick={handleButtonClick}
                                buttonTitle={solution.fields.buttonText}
                                backgroundColor={solution.fields.backgroundColor}
                                // xng-change: add google event
                                handleClickCard={(title: string, url: string | undefined) =>
                                    xngGaGeneralEventEvent({
                                        eventType: GOOGLE_EVENTS.OUR_SOLUTIONS,
                                        includeEvent: xGoogleAnalytics.includeOurSolutionsEvent,
                                        clickText: title,
                                        destination: url,
                                    })
                                }
                            />
                        );
                    })}
                </XngCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SolutionsWidget)),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "background Color",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                defaultValue: "",
            },
            ...(sectionTitleFields as any),
            {
                name: fields.solutions,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: solutionFields.buttonText,
                        displayName: "Button text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: solutionFields.backgroundColor,
                        displayName: "background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                    {
                        name: solutionFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: solutionFields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: solutionFields.destination,
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
